'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../state/cart';
import { useWishlist } from '../state/wishlist';
import { useCustomerAuth } from '../state/customerAuth';
import { fetchShopProducts } from '../api/catalog';
import { slugFromProductUrl } from '../data/mockCatalog';

export function ThemeNavbar() {
  const { itemCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { isAuthenticated } = useCustomerAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);

  const [q, setQ] = useState('');
  const [products, setProducts] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchWrapRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    setMenuOpen(false);
    setPolicyOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
      if (e.key === 'Escape') {
        setPolicyOpen(false);
        setSearchOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    (async () => {
      try {
        const { products: list } = await fetchShopProducts(ac.signal);
        if (!cancelled) setProducts(Array.isArray(list) ? list : []);
      } catch (_) {
        if (!cancelled) setProducts([]);
      }
    })();
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      const el = searchWrapRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const bodyScroll = window.scrollY;
      const navbar = document.querySelector('.navbar');
      if (!navbar) return;
      if (bodyScroll > 300) navbar.classList.add('nav-scroll');
      else navbar.classList.remove('nav-scroll');
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const suggestions = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query || query.length < 1) return [];
    const scored = products
      .map((p) => {
        const name = String(p?.name || '').toLowerCase();
        const slug = String(p?.slug || slugFromProductUrl(p?.url) || '').toLowerCase();
        if (!name && !slug) return null;
        let score = 0;
        if (slug === query) score += 100;
        if (name === query) score += 90;
        if (slug.startsWith(query)) score += 70;
        if (name.startsWith(query)) score += 60;
        if (slug.includes(query)) score += 40;
        if (name.includes(query)) score += 30;
        if (score <= 0) return null;
        return { p, slug: p?.slug || slugFromProductUrl(p?.url), score };
      })
      .filter(Boolean)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6);
    return scored;
  }, [q, products]);

  const goToProduct = (slug) => {
    const s = String(slug || '').trim();
    if (!s) return;
    setMenuOpen(false);
    setPolicyOpen(false);
    setSearchOpen(false);
    navigate(`/product/${encodeURIComponent(s)}`);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const query = q.trim();
    if (!query) return;

    const exact = products.find((p) => {
      const slug = String(p?.slug || slugFromProductUrl(p?.url) || '');
      return slug.toLowerCase() === query.toLowerCase();
    });
    if (exact) {
      goToProduct(exact.slug || slugFromProductUrl(exact.url));
      return;
    }

    if (suggestions[0]?.slug) {
      goToProduct(suggestions[0].slug);
      return;
    }

    // fallback: take user to shop
    setMenuOpen(false);
    setPolicyOpen(false);
    setSearchOpen(false);
    navigate('/shop');
  };

  return (
    <nav
      className={`navbar navbar-expand-lg bord blur${menuOpen ? ' navbar--menu-open' : ''}`}
    >
      {menuOpen && (
        <button
          type="button"
          className="elenardNavBackdrop"
          aria-label="Close menu"
          tabIndex={-1}
          onClick={() => setMenuOpen(false)}
        />
      )}

      <div className="container elenardNavBarInner">
        <Link className="logo icon-img-100 elenardNavBrand" to="/" aria-label="Elena RD home">
          <img src="/assets/imgs/logo-light.svg" alt="Elena RD" />
        </Link>

        <div className="elenardNavRight">
          <div className="elenardNavQuick" aria-label="Quick links">
            <NavLink
              to="/wishlist"
              className={({ isActive }) =>
                `elenardNavIconBtn${isActive ? ' elenardNavIconBtn--active' : ''}`
              }
              aria-label={`Wishlist, ${wishlistCount} saved`}
              onClick={() => setMenuOpen(false)}
            >
              <i
                className={wishlistCount > 0 ? 'fas fa-heart' : 'far fa-heart'}
                aria-hidden="true"
              />
              {wishlistCount > 0 ? (
                <span className="elenardNavBadge">{wishlistCount}</span>
              ) : null}
            </NavLink>
            <NavLink
              to="/cart"
              className={({ isActive }) =>
                `elenardNavIconBtn${isActive ? ' elenardNavIconBtn--active' : ''}`
              }
              aria-label={`Shopping cart, ${itemCount} items`}
              onClick={() => setMenuOpen(false)}
            >
              <i className="fas fa-shopping-cart" aria-hidden="true" />
              {itemCount > 0 ? <span className="elenardNavBadge">{itemCount}</span> : null}
            </NavLink>
          </div>

          <button
            className={`navbar-toggler elenardNavToggler d-lg-none${menuOpen ? '' : ' collapsed'}`}
            type="button"
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
            aria-controls="elenardNavbarNav"
            id="elenardNavbarToggler"
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className="icon-bar">
              <i className="fas fa-bars" aria-hidden="true" />
            </span>
          </button>
        </div>

        <div
          id="elenardNavbarNav"
          className={`collapse navbar-collapse justify-content-center elenardNavCollapse${menuOpen ? ' show' : ''}`}
        >
          {isAuthenticated ? (
            <div className="elenardNavSearch" ref={searchWrapRef}>
              <form className="elenardNavSearchForm" onSubmit={onSearchSubmit} role="search">
                <input
                  className="elenardNavSearchInput"
                  type="search"
                  ref={searchInputRef}
                  value={q}
                  placeholder="Search products…"
                  onChange={(e) => {
                    setQ(e.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  aria-label="Search products"
                />
                <button className="elenardNavSearchBtn" type="submit" aria-label="Search">
                  <i className="fas fa-search" aria-hidden="true" />
                </button>
              </form>

              {searchOpen && q.trim() && (
                <div className="elenardNavSearchMenu" role="listbox" aria-label="Search suggestions">
                  {suggestions.length > 0 ? (
                    suggestions.map(({ p, slug }) => (
                      <button
                        key={p?.numericId != null ? `s-${p.numericId}` : p?.url || slug}
                        type="button"
                        className="elenardNavSearchItem"
                        onClick={() => goToProduct(slug)}
                      >
                        <span className="elenardNavSearchItemName">{p?.name || slug}</span>
                        <span className="elenardNavSearchItemMeta">View</span>
                      </button>
                    ))
                  ) : (
                    <div className="elenardNavSearchItem" style={{ cursor: 'default' }}>
                      <span className="elenardNavSearchItemName">No matching products</span>
                      <span className="elenardNavSearchItemMeta">Try another keyword</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="elenardNavSearch" ref={searchWrapRef}>
              <form className="elenardNavSearchForm" onSubmit={onSearchSubmit} role="search">
                <input
                  className="elenardNavSearchInput"
                  type="search"
                  ref={searchInputRef}
                  value={q}
                  placeholder="Search products…"
                  onChange={(e) => {
                    setQ(e.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  aria-label="Search products"
                />
                <button className="elenardNavSearchBtn" type="submit" aria-label="Search">
                  <i className="fas fa-search" aria-hidden="true" />
                </button>
              </form>

              {searchOpen && q.trim() && (
                <div className="elenardNavSearchMenu" role="listbox" aria-label="Search suggestions">
                  {suggestions.length > 0 ? (
                    suggestions.map(({ p, slug }) => (
                      <button
                        key={p?.numericId != null ? `s-${p.numericId}` : p?.url || slug}
                        type="button"
                        className="elenardNavSearchItem"
                        onClick={() => goToProduct(slug)}
                      >
                        <span className="elenardNavSearchItemName">{p?.name || slug}</span>
                        <span className="elenardNavSearchItemMeta">View</span>
                      </button>
                    ))
                  ) : (
                    <div className="elenardNavSearchItem" style={{ cursor: 'default' }}>
                      <span className="elenardNavSearchItemName">No matching products</span>
                      <span className="elenardNavSearchItemMeta">Try another keyword</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <ul className="navbar-nav">
            <li className="nav-item">
              <NavLink className="nav-link" to="/" end onClick={() => setMenuOpen(false)}>
                <span className="rolling-text">Home</span>
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink className="nav-link" to="/shop" onClick={() => setMenuOpen(false)}>
                <span className="rolling-text">Shop Online</span>
              </NavLink>
            </li>
            <li className={`nav-item elenardNavDropdown${policyOpen ? ' elenardNavDropdown--open' : ''}`}>
              <button
                type="button"
                className="nav-link elenardNavDropdownBtn"
                aria-haspopup="menu"
                aria-expanded={policyOpen}
                onClick={() => setPolicyOpen((v) => !v)}
              >
                <span className="rolling-text">Policy</span>
                <span className="elenardNavDropdownCaret" aria-hidden="true">▾</span>
              </button>
              <div className="elenardNavDropdownMenu" role="menu">
                <NavLink className="elenardNavDropdownLink" to="/policy/privacy" role="menuitem" onClick={() => setMenuOpen(false)}>
                  Privacy Policy
                </NavLink>
                <NavLink className="elenardNavDropdownLink" to="/policy/store" role="menuitem" onClick={() => setMenuOpen(false)}>
                  Store Policy
                </NavLink>
              </div>
            </li>
            <li className="nav-item">
              <NavLink className="nav-link" to="/contact" onClick={() => setMenuOpen(false)}>
                <span className="rolling-text">Contact Us</span>
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink className="nav-link" to={isAuthenticated ? '/account/dashboard' : '/account'} onClick={() => setMenuOpen(false)}>
                <span className="rolling-text">{isAuthenticated ? 'My account' : 'Login'}</span>
              </NavLink>
            </li>
            <li className="nav-item d-lg-none">
              <NavLink className="nav-link" to="/wishlist" onClick={() => setMenuOpen(false)}>
                <span className="rolling-text">Wishlist ({wishlistCount})</span>
              </NavLink>
            </li>
            <li className="nav-item d-lg-none">
              <NavLink className="nav-link" to="/cart" onClick={() => setMenuOpen(false)}>
                <span className="rolling-text">Cart ({itemCount})</span>
              </NavLink>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
}
